const PDF_VIEWER_SELECTOR = '[data-pdf-viewer]';
const PDF_JS_VERSION = '5.7.284';
const PDF_JS_MODULE_URL = `https://unpkg.com/pdfjs-dist@${PDF_JS_VERSION}/build/pdf.min.mjs`;
const PDF_JS_WORKER_URL = `https://unpkg.com/pdfjs-dist@${PDF_JS_VERSION}/build/pdf.worker.min.mjs`;
const SAFE_PDF_FILENAME_PATTERN = /^[A-Za-z0-9._-]+\.pdf$/i;
const MIN_ZOOM = 0.6;
const MAX_ZOOM = 2.4;
const ZOOM_STEP = 0.1;
const THUMB_TARGET_WIDTH = 108;
const DESKTOP_MEDIA_QUERY = '(min-width: 961px)';

async function loadPdfJs() {
  if (window.__amPdfJsModule) return window.__amPdfJsModule;

  const pdfjsLib = await import(PDF_JS_MODULE_URL);
  pdfjsLib.GlobalWorkerOptions.workerSrc = PDF_JS_WORKER_URL;
  window.__amPdfJsModule = pdfjsLib;
  return pdfjsLib;
}

function getViewerParams(root) {
  const params = new URLSearchParams(window.location.search);
  const fallbackReturnUrl = root.dataset.returnUrl || window.Shopify?.routes?.root || '/';

  return {
    file: params.get('file') || '',
    title: params.get('title') || '',
    returnUrl: params.get('return') || fallbackReturnUrl
  };
}

function isSafePdfFile(fileName) {
  return SAFE_PDF_FILENAME_PATTERN.test(fileName);
}

function buildPdfUrl(fileName) {
  return `/cdn/shop/files/${encodeURIComponent(fileName)}`;
}

function normalizeReturnUrl(returnUrl, fallbackUrl) {
  if (!returnUrl) return fallbackUrl;

  try {
    const resolved = new URL(returnUrl, window.location.origin);
    if (resolved.origin !== window.location.origin) return fallbackUrl;
    return `${resolved.pathname}${resolved.search}${resolved.hash}`;
  } catch (error) {
    return fallbackUrl;
  }
}

function formatPresentationTitle(title) {
  const cleanTitle = (title || '').trim();
  if (!cleanTitle) return 'Presentation';
  if (/(presentation|catalogue|catalog|lookbook|pdf)$/i.test(cleanTitle)) return cleanTitle;
  return `${cleanTitle} Presentation`;
}

function setStatus(root, message, isError = false) {
  const status = root.querySelector('[data-pdf-viewer-status]');
  if (!status) return;

  status.textContent = message || '';
  status.hidden = !message;
  status.classList.toggle('is-error', Boolean(isError));
}

function setTitle(root, title) {
  const titleNode = root.querySelector('[data-pdf-viewer-title]');
  if (titleNode) titleNode.textContent = title;

  if (!root.__pdfViewerDocumentBaseTitle) {
    const titleParts = document.title.split('|').map((part) => part.trim()).filter(Boolean);
    root.__pdfViewerDocumentBaseTitle = titleParts.length > 1 ? titleParts[titleParts.length - 1] : document.title;
  }

  document.title = `${title} | ${root.__pdfViewerDocumentBaseTitle}`;
}

function getScrollContainer(root) {
  return root.querySelector('[data-pdf-viewer-scroll]');
}

function getPagesContainer(root) {
  return root.querySelector('[data-pdf-viewer-pages]');
}

function getThumbsWrap(root) {
  return root.querySelector('[data-pdf-viewer-thumbs-wrap]');
}

function getThumbsContainer(root) {
  return root.querySelector('[data-pdf-viewer-thumbs]');
}

function toggleEmptyState(root, isVisible, title, message) {
  const emptyState = root.querySelector('[data-pdf-viewer-empty]');
  const scroll = getScrollContainer(root);
  const toolbar = root.querySelector('[data-pdf-viewer-toolbar]');
  const thumbsWrap = getThumbsWrap(root);

  if (emptyState) {
    emptyState.hidden = !isVisible;
    const emptyTitle = emptyState.querySelector('[data-pdf-viewer-empty-title]');
    const emptyCopy = emptyState.querySelector('[data-pdf-viewer-empty-copy]');
    if (emptyTitle && title) emptyTitle.textContent = title;
    if (emptyCopy && message) emptyCopy.textContent = message;
  }

  if (scroll) scroll.hidden = isVisible;
  if (toolbar) toolbar.hidden = isVisible;
  if (thumbsWrap) thumbsWrap.hidden = isVisible;
}

function downloadPdf(root, pdfUrl, fileName) {
  const downloadLink = document.createElement('a');
  downloadLink.href = pdfUrl;
  downloadLink.download = fileName || '';
  downloadLink.rel = 'noopener';
  document.body.appendChild(downloadLink);
  downloadLink.click();
  downloadLink.remove();
  setStatus(root, root.dataset.downloadMessage || 'Presentation downloaded.', false);
  window.clearTimeout(root.__pdfViewerStatusTimeout);
  root.__pdfViewerStatusTimeout = window.setTimeout(() => {
    setStatus(root, '', false);
  }, 2000);
}

function redirectToReturnUrl(returnUrl) {
  if (!returnUrl) return;
  window.location.replace(returnUrl);
}

function clampZoom(value) {
  return Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, value));
}

function getDefaultZoom() {
  return window.matchMedia(DESKTOP_MEDIA_QUERY).matches ? 0.9 : 1;
}

function getPageContentWidth(root) {
  const scroll = getScrollContainer(root);
  if (!scroll) return Math.max(320, window.innerWidth - 48);

  const styles = window.getComputedStyle(scroll);
  const paddingX = parseFloat(styles.paddingLeft || '0') + parseFloat(styles.paddingRight || '0');
  return Math.max(320, scroll.clientWidth - paddingX);
}

function updateZoomLabel(root) {
  const label = root.querySelector('[data-pdf-zoom-label]');
  const state = root.__pdfViewerState;
  if (!label || !state) return;

  label.textContent = `${Math.round(state.zoom * 100)}%`;
}

function syncPanMode(root) {
  const state = root.__pdfViewerState;
  const scroll = getScrollContainer(root);
  if (!state || !scroll) return;

  const isPanEnabled = state.zoom > 1.001;
  root.classList.toggle('is-pan-enabled', isPanEnabled);
  scroll.classList.toggle('is-pan-enabled', isPanEnabled);
}

function setActiveThumbnail(root, pageNumber) {
  const thumbs = getThumbsContainer(root);
  if (!thumbs) return;

  thumbs.querySelectorAll('[data-thumb-page]').forEach((thumb) => {
    const thumbPage = Number(thumb.getAttribute('data-thumb-page'));
    const isActive = thumbPage === pageNumber;
    thumb.classList.toggle('is-active', isActive);
    if (isActive) {
      thumb.setAttribute('aria-current', 'page');
    } else {
      thumb.removeAttribute('aria-current');
    }
  });
}

function updatePageControls(root) {
  const state = root.__pdfViewerState;
  if (!state?.pdf) return;

  const currentPage = Math.min(state.pdf.numPages, Math.max(1, state.currentPage || 1));
  const prevButton = root.querySelector('[data-pdf-page-prev]');
  const nextButton = root.querySelector('[data-pdf-page-next]');
  const current = root.querySelector('[data-pdf-page-current]');
  const total = root.querySelector('[data-pdf-page-total]');

  state.currentPage = currentPage;

  if (current) current.textContent = String(currentPage);

  if (total) total.textContent = `/ ${state.pdf.numPages}`;
  if (prevButton) prevButton.disabled = currentPage <= 1;
  if (nextButton) nextButton.disabled = currentPage >= state.pdf.numPages;

  setActiveThumbnail(root, currentPage);
}

function getCurrentPageFromScroll(root) {
  const state = root.__pdfViewerState;
  const scroll = getScrollContainer(root);
  if (!state?.pdf || !scroll) return 1;

  const pageNodes = Array.from(root.querySelectorAll('[data-page-item]'));
  if (!pageNodes.length) return 1;

  const checkpoint = scroll.scrollTop + (scroll.clientHeight * 0.22);
  let currentPage = 1;

  pageNodes.forEach((pageNode) => {
    const pageNumber = Number(pageNode.getAttribute('data-page-item'));
    if (!pageNumber) return;

    if (checkpoint >= pageNode.offsetTop - 8) {
      currentPage = pageNumber;
    }
  });

  return currentPage;
}

function scrollToPage(root, pageNumber, behavior = 'smooth') {
  const state = root.__pdfViewerState;
  const scroll = getScrollContainer(root);
  const pagesContainer = getPagesContainer(root);
  if (!state?.pdf || !scroll || !pagesContainer) return;

  const safePage = Math.min(state.pdf.numPages, Math.max(1, Number(pageNumber) || 1));
  const pageNode = pagesContainer.querySelector(`[data-page-item="${safePage}"]`);

  state.currentPage = safePage;
  updatePageControls(root);

  if (!pageNode) return;

  state.isProgrammaticScroll = true;
  scroll.scrollTo({
    top: safePage === 1 ? 0 : Math.max(0, pageNode.offsetTop - 8),
    behavior
  });

  window.clearTimeout(state.programmaticScrollTimeout);
  state.programmaticScrollTimeout = window.setTimeout(() => {
    if (root.__pdfViewerState) {
      root.__pdfViewerState.isProgrammaticScroll = false;
      root.__pdfViewerState.currentPage = getCurrentPageFromScroll(root);
      updatePageControls(root);
    }
  }, behavior === 'smooth' ? 450 : 120);
}

function bindPageTracking(root) {
  const state = root.__pdfViewerState;
  const scroll = getScrollContainer(root);
  if (!state || !scroll) return;

  if (state.scrollHandlerBound) return;
  state.scrollHandlerBound = true;

  scroll.addEventListener('scroll', () => {
    const activePage = getCurrentPageFromScroll(root);
    if (!activePage || activePage === state.currentPage) return;
    state.currentPage = activePage;
    updatePageControls(root);
  }, { passive: true });
}

async function renderPageNode(pdf, pageNumber, scale) {
  const page = await pdf.getPage(pageNumber);
  const viewport = page.getViewport({ scale });
  const outputScale = window.devicePixelRatio || 1;

  const pageNode = document.createElement('article');
  pageNode.className = 'pdf-viewer__page';
  pageNode.setAttribute('data-page-item', String(pageNumber));

  const canvas = document.createElement('canvas');
  canvas.className = 'pdf-viewer__page-canvas';
  canvas.width = Math.floor(viewport.width * outputScale);
  canvas.height = Math.floor(viewport.height * outputScale);
  canvas.style.width = `${viewport.width}px`;
  canvas.style.height = `${viewport.height}px`;

  pageNode.appendChild(canvas);

  const context = canvas.getContext('2d', { alpha: false });
  await page.render({
    canvasContext: context,
    transform: outputScale !== 1 ? [outputScale, 0, 0, outputScale, 0, 0] : null,
    viewport
  }).promise;

  return pageNode;
}

async function renderThumbnailNode(pdf, pageNumber) {
  const page = await pdf.getPage(pageNumber);
  const baseViewport = page.getViewport({ scale: 1 });
  const thumbScale = THUMB_TARGET_WIDTH / baseViewport.width;
  const viewport = page.getViewport({ scale: thumbScale });
  const outputScale = Math.min(window.devicePixelRatio || 1, 2);

  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'pdf-viewer__thumb';
  button.setAttribute('data-thumb-page', String(pageNumber));
  button.setAttribute('aria-label', `Go to page ${pageNumber}`);

  const canvas = document.createElement('canvas');
  canvas.className = 'pdf-viewer__thumb-canvas';
  canvas.width = Math.floor(viewport.width * outputScale);
  canvas.height = Math.floor(viewport.height * outputScale);
  canvas.style.width = `${viewport.width}px`;
  canvas.style.height = `${viewport.height}px`;

  const context = canvas.getContext('2d', { alpha: false });
  await page.render({
    canvasContext: context,
    transform: outputScale !== 1 ? [outputScale, 0, 0, outputScale, 0, 0] : null,
    viewport
  }).promise;

  const label = document.createElement('span');
  label.className = 'pdf-viewer__thumb-label';
  label.textContent = String(pageNumber);

  button.appendChild(canvas);
  button.appendChild(label);

  return button;
}

async function renderThumbnails(root) {
  const state = root.__pdfViewerState;
  const thumbsWrap = getThumbsWrap(root);
  const thumbsContainer = getThumbsContainer(root);
  if (!state?.pdf || !thumbsWrap || !thumbsContainer) return;

  thumbsContainer.replaceChildren();
  thumbsContainer.setAttribute('aria-busy', 'true');

  try {
    const fragment = document.createDocumentFragment();

    for (let pageNumber = 1; pageNumber <= state.pdf.numPages; pageNumber += 1) {
      const thumbNode = await renderThumbnailNode(state.pdf, pageNumber);
      fragment.appendChild(thumbNode);
    }

    thumbsContainer.appendChild(fragment);
    thumbsWrap.hidden = false;
    updatePageControls(root);
  } finally {
    thumbsContainer.removeAttribute('aria-busy');
  }
}

async function renderAllPages(root, options = {}) {
  const state = root.__pdfViewerState;
  const pagesContainer = getPagesContainer(root);
  if (!state?.pdf || !pagesContainer) return;

  const targetPage = options.targetPage || state.currentPage || 1;
  const scale = state.fitScale * state.zoom;
  pagesContainer.setAttribute('aria-busy', 'true');
  pagesContainer.classList.add('is-rendering');

  try {
    const nextPagesContainer = document.createElement('div');
    nextPagesContainer.className = pagesContainer.className;
    nextPagesContainer.setAttribute('data-pdf-viewer-pages', '');

    for (let pageNumber = 1; pageNumber <= state.pdf.numPages; pageNumber += 1) {
      const pageNode = await renderPageNode(state.pdf, pageNumber, scale);
      nextPagesContainer.appendChild(pageNode);
    }

    pagesContainer.replaceWith(nextPagesContainer);
    state.currentPage = targetPage;
    updatePageControls(root);
    bindPageTracking(root);
    toggleEmptyState(root, false);

    window.requestAnimationFrame(() => {
      scrollToPage(root, targetPage, 'auto');
    });
  } finally {
    const activePagesContainer = getPagesContainer(root);
    if (activePagesContainer) {
      activePagesContainer.removeAttribute('aria-busy');
      activePagesContainer.classList.remove('is-rendering');
    }
  }
}

function computeFitScale(root, pdf) {
  const state = root.__pdfViewerState;
  if (state?.maxBasePageWidth) {
    return Promise.resolve(getPageContentWidth(root) / state.maxBasePageWidth);
  }

  const pagePromises = [];
  for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber += 1) {
    pagePromises.push(
      pdf.getPage(pageNumber).then((page) => page.getViewport({ scale: 1 }).width)
    );
  }

  return Promise.all(pagePromises).then((widths) => {
    const widestPage = Math.max(...widths);
    if (root.__pdfViewerState) {
      root.__pdfViewerState.maxBasePageWidth = widestPage;
    }
    return getPageContentWidth(root) / widestPage;
  });
}

async function setZoom(root, nextZoom) {
  const state = root.__pdfViewerState;
  if (!state?.pdf) return;

  state.zoom = clampZoom(nextZoom);
  updateZoomLabel(root);
  syncPanMode(root);
  await renderAllPages(root, { targetPage: state.currentPage });
}

async function fitToWidth(root) {
  const state = root.__pdfViewerState;
  if (!state?.pdf) return;

  state.fitScale = await computeFitScale(root, state.pdf);
  updateZoomLabel(root);
  syncPanMode(root);
  await renderAllPages(root, { targetPage: state.currentPage || 1 });
}

function bindActions(root, pdfUrl, fileName, returnUrl) {
  if (root.dataset.pdfViewerActionsBound === 'true') return;
  root.dataset.pdfViewerActionsBound = 'true';

  const backButton = root.querySelector('[data-pdf-viewer-back]');
  const downloadButton = root.querySelector('[data-pdf-viewer-download]');

  if (backButton) {
    backButton.addEventListener('click', () => {
      const sameOriginReferrer = document.referrer && (() => {
        try {
          return new URL(document.referrer).origin === window.location.origin;
        } catch (error) {
          return false;
        }
      })();

      if (window.history.length > 1 && sameOriginReferrer) {
        window.history.back();
        return;
      }

      window.location.href = returnUrl;
    });
  }

  if (downloadButton) {
    downloadButton.addEventListener('click', () => {
      downloadPdf(root, pdfUrl, fileName);
    });
  }
}

function bindZoomControls(root) {
  if (root.dataset.pdfZoomBound === 'true') return;
  root.dataset.pdfZoomBound = 'true';

  const zoomIn = root.querySelector('[data-pdf-zoom-in]');
  const zoomOut = root.querySelector('[data-pdf-zoom-out]');

  if (zoomIn) {
    zoomIn.addEventListener('click', () => {
      const state = root.__pdfViewerState;
      if (!state) return;
      setZoom(root, state.zoom + ZOOM_STEP);
    });
  }

  if (zoomOut) {
    zoomOut.addEventListener('click', () => {
      const state = root.__pdfViewerState;
      if (!state) return;
      setZoom(root, state.zoom - ZOOM_STEP);
    });
  }
}

function bindPageControls(root) {
  if (root.dataset.pdfPageControlsBound === 'true') return;
  root.dataset.pdfPageControlsBound = 'true';

  const prevButton = root.querySelector('[data-pdf-page-prev]');
  const nextButton = root.querySelector('[data-pdf-page-next]');

  if (prevButton) {
    prevButton.addEventListener('click', () => {
      const state = root.__pdfViewerState;
      if (!state) return;
      scrollToPage(root, state.currentPage - 1);
    });
  }

  if (nextButton) {
    nextButton.addEventListener('click', () => {
      const state = root.__pdfViewerState;
      if (!state) return;
      scrollToPage(root, state.currentPage + 1);
    });
  }
}

function bindThumbnailNavigation(root) {
  if (root.dataset.pdfThumbsBound === 'true') return;
  root.dataset.pdfThumbsBound = 'true';

  const thumbsContainer = getThumbsContainer(root);
  if (!thumbsContainer) return;

  thumbsContainer.addEventListener('click', (event) => {
    const thumbButton = event.target.closest('[data-thumb-page]');
    if (!thumbButton) return;
    scrollToPage(root, Number(thumbButton.getAttribute('data-thumb-page')));
  });
}

function bindResize(root) {
  if (root.dataset.pdfResizeBound === 'true') return;
  root.dataset.pdfResizeBound = 'true';

  window.addEventListener('resize', () => {
    const state = root.__pdfViewerState;
    if (!state?.pdf) return;

    window.clearTimeout(state.resizeTimeout);
    state.resizeTimeout = window.setTimeout(async () => {
      const freshState = root.__pdfViewerState;
      if (!freshState?.pdf) return;

      freshState.fitScale = await computeFitScale(root, freshState.pdf);
      updateZoomLabel(root);
      syncPanMode(root);
      await renderAllPages(root, { targetPage: freshState.currentPage || 1 });
    }, 180);
  });
}

async function loadDocument(root, pdfUrl, title) {
  const pagesContainer = getPagesContainer(root);
  const thumbsContainer = getThumbsContainer(root);
  if (!pagesContainer) return;

  pagesContainer.replaceChildren();
  if (thumbsContainer) thumbsContainer.replaceChildren();
  setStatus(root, '', false);
  toggleEmptyState(root, false);

  try {
    const pdfjsLib = await loadPdfJs();
    const pdf = await pdfjsLib.getDocument({
      url: pdfUrl,
      cMapPacked: true,
      withCredentials: false
    }).promise;

    root.__pdfViewerState = {
      pdf,
      fitScale: 1,
      zoom: getDefaultZoom(),
      currentPage: 1,
      maxBasePageWidth: null,
      resizeTimeout: null,
      programmaticScrollTimeout: null,
      isProgrammaticScroll: false,
      scrollHandlerBound: false
    };

    setTitle(root, formatPresentationTitle(title));
    updateZoomLabel(root);
    syncPanMode(root);
    await renderThumbnails(root);
    await fitToWidth(root);
  } catch (error) {
    root.__pdfViewerState = null;
    pagesContainer.replaceChildren();
    if (thumbsContainer) thumbsContainer.replaceChildren();

    toggleEmptyState(
      root,
      true,
      root.dataset.errorTitle || 'Unable to open this PDF',
      root.dataset.errorMessage || 'The file link is missing or the PDF could not be loaded.'
    );
    setStatus(root, '', false);
  }
}

async function initPdfViewer(root) {
  if (!root || root.dataset.pdfViewerBound === 'true') return;
  root.dataset.pdfViewerBound = 'true';

  const { file, title, returnUrl } = getViewerParams(root);
  const fallbackTitle = root.dataset.defaultTitle || 'Presentation';
  const safeReturnUrl = normalizeReturnUrl(returnUrl, root.dataset.returnUrl || '/');
  const currentPageKey = `${window.location.pathname}${window.location.search}`;

  if (!isSafePdfFile(file)) {
    if (safeReturnUrl && safeReturnUrl !== currentPageKey) {
      redirectToReturnUrl(safeReturnUrl);
      return;
    }

    redirectToReturnUrl(root.dataset.returnUrl || '/');
    return;
  }

  const pdfUrl = buildPdfUrl(file);
  const resolvedTitle = (title || file.replace(/\.pdf$/i, '').replace(/[-_]+/g, ' ')).trim() || fallbackTitle;

  bindActions(root, pdfUrl, file, safeReturnUrl);
  bindZoomControls(root);
  bindPageControls(root);
  bindThumbnailNavigation(root);
  bindResize(root);
  await loadDocument(root, pdfUrl, resolvedTitle);
}

function bootPdfViewers() {
  document.querySelectorAll(PDF_VIEWER_SELECTOR).forEach((root) => {
    initPdfViewer(root);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', bootPdfViewers, { once: true });
} else {
  bootPdfViewers();
}
